import {clamp} from "lodash";
import {FocusEvent, KeyboardEventHandler, KeyboardEvent} from "react";
import {Actions, CommonProps} from "./types";

type HandlerProps = Required<Actions & CommonProps>;

export const onBlur =
  (props: Pick<HandlerProps, "reset" | "onBlur">): Actions["onBlur"] =>
  (e) => {
    // blur only when an element outside of the dropdown (relatedTarget) gets focus.
    // for ie11, use document.activeElement instead.
    if (
      !e.currentTarget.contains(
        (e.relatedTarget as Node) || document.activeElement
      )
    ) {
      props.reset();
      props.onBlur(e);
    }
  };

export const moveHighlightedIndex = (
  amount: number,
  {
    highlightedIndex,
    options,
    onOptionHighlighted,
    menuRef,
  }: Pick<
    HandlerProps,
    "highlightedIndex" | "options" | "onOptionHighlighted" | "menuRef"
  >
): void => {
  const newIndex = clamp(
    amount + highlightedIndex,
    0,
    options.length && options.length - 1
  );

  if (newIndex !== highlightedIndex) onOptionHighlighted(newIndex);

  if (menuRef && menuRef.current && menuRef.current.firstElementChild) {
    const optionHeight = menuRef.current.firstElementChild.clientHeight;
    const optionMarginTop = 20;
    /* eslint-disable no-param-reassign */
    menuRef.current.scrollTop =
      newIndex === 1 ? 0 : optionHeight * newIndex - optionMarginTop;
  }
};

export const isolateEvent = (e: KeyboardEvent | FocusEvent): void => {
  e.preventDefault();
  e.stopPropagation();
};

export const controlOnKeyDown =
  ({
    menuIsOpen,
    onMenuOpen,
  }: Pick<HandlerProps, "menuIsOpen" | "onMenuOpen">): KeyboardEventHandler =>
  (e) => {
    if (!menuIsOpen && ["Enter", " ", "ArrowUp", "ArrowDown"].includes(e.key)) {
      isolateEvent(e);
      onMenuOpen();
    }
  };

export const menuOnKeyDown =
  (
    props: Pick<
      HandlerProps,
      | "menuIsOpen"
      | "highlightedIndex"
      | "onChange"
      | "options"
      | "onOptionHighlighted"
      | "reset"
      | "menuRef"
    >
  ): KeyboardEventHandler =>
  (e) => {
    if (props.menuIsOpen) {
      if (e.key === "ArrowUp") {
        isolateEvent(e);
        moveHighlightedIndex(-1, props);
      } else if (e.key === "ArrowDown") {
        isolateEvent(e);
        moveHighlightedIndex(1, props);
      } else if (["Enter", " "].includes(e.key)) {
        isolateEvent(e);
        props.onChange(
          props.options[props.highlightedIndex],
          props.highlightedIndex
        );
      } else if (e.key === "Escape") {
        isolateEvent(e);
        props.reset();
      }
    }
  };
